import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { CardTile, CardGrid } from "@/components/CardTile";
import { PageTitle, Spinner, Empty, Btn } from "@/components/UI";
import { CardView, FilterPanel } from '@/components/CardBrowser';
import { AddSelection } from '@/components/AddSelection';
import { displayCards } from '@/lib/setCards';
import { useAuth } from '@/context/AuthContext';

const searchPages = new Map();

const CATS = ["Pokemon", "Trainer", "Energy"];
const SORTS = [{ v: "none", l: "Default order" }, { v: "name", l: "Name A–Z" }, { v: "-name", l: "Name Z–A" }, { v: "localId", l: "Card number" }, { v: "-price", l: "Price: high to low" }, { v: "price", l: "Price: low to high" }, { v: "-hp", l: "Highest HP" }];

export default function Search() {
  const { user } = useAuth() || {};
  const [sp, setSp] = useSearchParams();
  const f = Object.fromEntries(["q", "set_id", "era", "type", "rarity", "category", "illustrator", "dex", "variant", "stamp", "ownership", "min_price", "max_price", "sort"].map((k) => [k, sp.get(k) || ""]));
  const [text, setText] = useState(f.q);
  const [meta, setMeta] = useState({ types: [], rarities: [], sets: [] });
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const key = sp.toString();
  const cacheKey = `${user?.id || 'anonymous'}:${key}`;
  const query = f.q;
  const requestRef = useRef(null);
  const selectionRequest = useRef(null);
  const [preparing, setPreparing] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [selection, setSelection] = useState({});
  const selected = Object.keys(selection);
  const printingSelections = Object.fromEntries(Object.entries(selection).filter(([, variants]) => variants?.length));
  const [selecting, setSelecting] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const sentinel = useRef(null);
  const busy = useRef(false);
  const finishes = card => card.variant ? [card.variant] : card.matched_variants;
  const isSelected = card => Object.prototype.hasOwnProperty.call(selection, card.id) && (!finishes(card)?.length || selection[card.id] === null || finishes(card).every(finish => selection[card.id]?.includes(finish)));
  const select = card => setSelection(old => {
    const next = { ...old }, required = finishes(card);
    if (!required?.length) { if (Object.prototype.hasOwnProperty.call(old, card.id)) delete next[card.id]; else next[card.id] = null; }
    else if (old[card.id] === null) delete next[card.id];
    else if (old[card.id]?.length && required.every(finish => old[card.id].includes(finish))) {
      const remaining = old[card.id].filter(finish => !required.includes(finish));
      if (remaining.length) next[card.id] = remaining; else delete next[card.id];
    } else next[card.id] = [...new Set([...(old[card.id] || []), ...required])];
    return next;
  });

  const setF = useCallback((k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n, { replace: true }); }, [sp, setSp]);
  useEffect(() => {
    const controller = new AbortController();
    api.get("/meta", { signal: controller.signal }).then((r) => { if (!controller.signal.aborted) setMeta(r.data); })
      .catch((e) => { if (!controller.signal.aborted) toast.error(formatErr(e)); });
    return () => controller.abort();
  }, []);
  useEffect(() => { setText(query); }, [query]);
  useEffect(() => { const t = setTimeout(() => text !== query && setF("q", text), 150); return () => clearTimeout(t); }, [text, query, setF]);

  const load = useCallback(async (p) => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    busy.current = true;
    setLoading(true);
    try {
      const filters = Object.fromEntries(new URLSearchParams(key));
      const params = { ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "none")), page: p, size: 36 };
      const { data } = await api.get("/cards", { params, signal: controller.signal });
      if (controller.signal.aborted) return;
      setItems((prev) => {
        const items = p === 1 ? data.items : [...prev, ...data.items];
        if (user?.id && !data.preparing) {
          searchPages.delete(cacheKey); searchPages.set(cacheKey, { items, page: p, more: data.has_more });
          if (searchPages.size > 20) searchPages.delete(searchPages.keys().next().value);
        }
        return items;
      });
      setPreparing(!!data.preparing); setProcessed(data.processed || 0);
      setMore(data.has_more);
      setPage(p);
    } catch (e) { if (!controller.signal.aborted) { setLoadError(true); toast.error(formatErr(e)); } }
    finally { if (requestRef.current === controller) busy.current = false; if (!controller.signal.aborted) setLoading(false); }
  }, [key, cacheKey, user?.id]);
  useEffect(() => { if (!preparing) return; const timer = setInterval(() => load(1), 2000); return () => clearInterval(timer); }, [preparing, load]);
  useEffect(() => {
    selectionRequest.current?.abort(); setSelecting(false);
    const saved = searchPages.get(cacheKey);
    setLoadError(false); setPreparing(false);
    if (saved) { setItems(saved.items); setPage(saved.page); setMore(saved.more); setLoading(false); }
    else { setPage(1); setMore(false); load(1); }
    return () => { requestRef.current?.abort(); selectionRequest.current?.abort(); };
  }, [load, cacheKey]);
  useEffect(() => {
    if (!more || loading || preparing || loadError || !sentinel.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting) && !busy.current) load(page + 1); }, { rootMargin: '500px' });
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [more, loading, preparing, loadError, load, page]);

  return <CardView view="search" controls={false}>{({ display, editButton, editor }) => (
    <div data-testid="search-page">
      <PageTitle title="Card search" sub="Every Pokémon TCG card ever printed — past, present and newly released.">{editButton}</PageTitle>{editor}
      <div className="space-y-4 rounded-3xl border border-slate-200/60 bg-white/80 p-4 backdrop-blur sm:p-6">
        <FilterPanel value={f} searchValue={text} onSearchChange={setText} onChange={next => { setText(next.q || ''); setSp(Object.fromEntries(Object.entries(next).filter(([, value]) => value)), { replace: true }); }} sorts={SORTS.map(sort => [sort.v, sort.l])} fields={[
          { key: 'q', label: 'Search' },
          { key: 'era', label: 'Era', options: (meta.eras || []).map(era => ({ value: era.id, label: era.name })) },
          { key: 'set_id', label: 'Set', options: meta.sets.filter(set => !f.era || set.era_id === f.era).map(set => ({ value: set.id, label: set.name, icon: set.icon })) },
          { key: 'type', label: 'Type', options: meta.types.map(value => ({ value, label: value })) },
          { key: 'stamp', label: 'Stamp', options: meta.stamps || [] },
          { key: 'rarity', label: 'Rarity', options: meta.rarities.map(value => ({ value, label: value })) },
          { key: 'category', label: 'Category', options: CATS.map(value => ({ value, label: value })) },
          { key: 'illustrator', label: 'Illustrator' }, { key: 'dex', label: 'Pokédex number' },
          { key: 'variant', label: 'Finish', options: ['normal', 'holo', 'reverse', 'firstEdition', 'wPromo'].map(value => ({ value, label: value })) },
          { key: 'ownership', label: 'Ownership', options: [{ value: 'owned', label: 'Owned' }, { value: 'missing', label: 'Missing' }] },
          { key: 'min_price', label: 'Minimum price (£)' }, { key: 'max_price', label: 'Maximum price (£)' },
        ]} />
      </div>
      <div className="mt-8">
        <div className="mb-4 flex flex-wrap items-center gap-3"><AddSelection ids={selected} printingSelections={printingSelections} watchRules={Object.fromEntries(Object.entries(f).filter(([, value]) => value))} onSaved={() => setSelection({})} /><Btn variant="ghost" disabled={selecting || preparing || loading} onClick={async () => {
          setSelecting(true);
          const controller = new AbortController(); selectionRequest.current = controller;
          try { const params = { ...Object.fromEntries(Object.entries(f).filter(([, value]) => value && value !== 'none')), selection: true }; const { data } = await api.get('/cards', { params, signal: controller.signal }); if (controller.signal.aborted) return; if (data.preparing) { toast.info('The filtered list is still preparing. Please try again when it is ready.'); return; } setSelection(Object.fromEntries(data.card_ids.map(cid => [cid, data.finish_overrides?.[cid] || null]))); } catch (error) { if (!controller.signal.aborted) toast.error(formatErr(error)); } finally { if (!controller.signal.aborted) setSelecting(false); }
        }}>Select all matching cards</Btn><Btn variant="ghost" onClick={() => setSelection({})}>Clear selection</Btn></div>
        {loading && items.length > 0 && page === 1 && <p role="status" className="mb-3 text-sm text-slate-500">Updating results…</p>}
        {preparing ? <Spinner text={`Building the persistent search index (${processed} cards indexed). This runs once, then refreshes in the background without blocking searches.`} /> : loading && page === 1 && !items.length ? <Spinner text="Searching cards…" /> : items.length === 0 ? (
          <Empty testid="search-empty" title="No cards found" text="Try a different name or remove some filters." />
        ) : (
          <CardGrid testid="search-results-grid">
            {displayCards(items, display).map((c, i) => <CardTile key={c.key || c.id} card={c} variant={c.variant} owned={c.owned} index={i % 36} selected={isSelected(c)} onSelect={select} />)}
          </CardGrid>
        )}
        <div ref={sentinel} data-testid="search-scroll-sentinel" className="h-4" />
        {more && !loading && !loadError && <p className="py-4 text-center text-sm text-slate-500">Scroll to load more cards</p>}
        {loadError && <Btn variant="ghost" onClick={() => { setLoadError(false); load(more ? page + 1 : 1); }}>Retry loading cards</Btn>}
        {loading && page > 1 && <Spinner />}
      </div>
    </div>
  )}</CardView>;
}
